import api from './api';
import { ScriptResponse, SeriesResponse } from '../types';

export interface SeriesOptions {
    seriesId?: number;
    newSeriesTitle?: string;
    finalPart?: boolean;
}

export const generateScript = async (
    topic: string,
    niche: string,
    tone: string = 'engaging',
    language: string = 'English',
    duration: string = '5',
    scriptFormat: string = 'EXPLAINER',
    seriesOptions?: SeriesOptions
): Promise<ScriptResponse> => {
    const response = await api.post('/api/scripts/generate', {
        topic,
        niche,
        tone,
        language,
        duration,
        scriptFormat,
        seriesId: seriesOptions?.seriesId,
        newSeriesTitle: seriesOptions?.newSeriesTitle,
        finalPart: seriesOptions?.finalPart,
    });
    return response.data;
};

export const getUserScripts = async (): Promise<ScriptResponse[]> => {
    const response = await api.get('/api/scripts/my-scripts');
    return response.data;
};

export const getUserSeries = async (): Promise<SeriesResponse[]> => {
    const response = await api.get('/api/scripts/series');
    return response.data;
};

// SHOT LIST: create (or fetch the saved) shot list for a script
export const generateShotList = async (
    scriptId: number,
    regenerate: boolean = false
): Promise<ScriptResponse> => {
    const response = await api.post(
        `/api/scripts/${scriptId}/shot-list`,
        null,
        { params: { regenerate }, timeout: 600000 } // long films can take several minutes
    );
    return response.data;
};

// CONTINUE: carry on an unfinished shot list from where it stopped
export const continueShotList = async (scriptId: number): Promise<ScriptResponse> => {
    const response = await api.post(
        `/api/scripts/${scriptId}/shot-list/continue`,
        null,
        { timeout: 600000 } // up to 10 minutes
    );
    return response.data;
};

// SHOT LIST: download the shot list as a PDF (PRO and Agency)
export const downloadShotListPdf = async (scriptId: number, title: string): Promise<void> => {
    const response = await api.get(`/api/scripts/${scriptId}/shot-list/pdf`, {
        responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    const safeTitle = (title || 'shot-list').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    link.href = url;
    link.download = `${safeTitle}-shot-list.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const translateToEnglish = async (text: string): Promise<string> => {
    const response = await api.post('/api/translate/to-english', text, {
        headers: { 'Content-Type': 'text/plain' }
    });
    return response.data;
};

// DELETE: move a script to Recently Deleted
export const deleteScript = async (scriptId: number): Promise<void> => {
    await api.delete(`/api/scripts/${scriptId}`);
};

// DELETE: bring a deleted script back
export const restoreScript = async (scriptId: number): Promise<ScriptResponse> => {
    const response = await api.post(`/api/scripts/${scriptId}/restore`);
    return response.data;
};

// DELETE: list deleted scripts
export const getDeletedScripts = async (): Promise<ScriptResponse[]> => {
    const response = await api.get('/api/scripts/deleted');
    return response.data;
};

// EDIT: save a user's edits to their script
export const updateScript = async (
    scriptId: number,
    scriptContent: string,
    title?: string
): Promise<ScriptResponse> => {
    const response = await api.put(`/api/scripts/${scriptId}`, {
        scriptContent,
        title,
    });
    return response.data;
};

// EDIT SHOT: save a user's corrections to their shot list
export const updateShotList = async (
    scriptId: number,
    shotList: string
): Promise<ScriptResponse> => {
    const response = await api.put(`/api/scripts/${scriptId}/shot-list`, {
        shotList,
    });
    return response.data;
};

// ONE SHOT: redo a single shot; returns the script with the updated shot list
export const regenerateShot = async (
    scriptId: number,
    shotNumber: number
): Promise<ScriptResponse> => {
    const response = await api.post(
        `/api/scripts/${scriptId}/shot-list/${shotNumber}/regenerate`,
        null,
        { timeout: 120000 } // up to 2 minutes
    );
    return response.data;
};

// VOICEOVER: types
export interface VoiceOption {
    voiceName: string;
    label: string;
    language: string;
}

export interface VoiceoverInfo {
    scriptId: number;
    voiceName: string;
    voiceLabel: string;
    voiceDescription: string | null;
    createdAt: string;
}

// VOICEOVER: the menu of voices
export const getVoiceOptions = async (): Promise<VoiceOption[]> => {
    const response = await api.get('/api/scripts/voices');
    return response.data;
};

// VOICEOVER: the saved voiceover details, or null if there is none yet
export const getVoiceover = async (scriptId: number): Promise<VoiceoverInfo | null> => {
    const response = await api.get(`/api/scripts/${scriptId}/voiceover`);
    return response.status === 204 || !response.data ? null : response.data;
};

// VOICEOVER: create or replace the voiceover (a chosen voice, or a description)
export const generateVoiceover = async (
    scriptId: number,
    voiceName?: string,
    voiceDescription?: string,
    includeDialogue: boolean = false
): Promise<VoiceoverInfo> => {
    const response = await api.post(
        `/api/scripts/${scriptId}/voiceover`,
        { voiceName, voiceDescription, includeDialogue },
        { timeout: 300000 } // long scripts can take a few minutes
    );
    return response.data;
};

// VOICEOVER: loads the MP3 and returns a link the audio player can use
export const getVoiceoverAudioUrl = async (scriptId: number): Promise<string> => {
    const response = await api.get(`/api/scripts/${scriptId}/voiceover/audio`, {
        responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
};

// VOICEOVER: downloads the MP3, named after the script title
export const downloadVoiceover = async (scriptId: number, title: string): Promise<void> => {
    const url = await getVoiceoverAudioUrl(scriptId);
    const link = document.createElement('a');
    const safeTitle = (title || 'voiceover').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    link.href = url;
    link.download = `${safeTitle}-voiceover.mp3`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// FINISHED SERIES: hide a series from the "Continue a series" dropdown
export const finishSeries = async (seriesId: number): Promise<void> => {
    await api.post(`/api/scripts/series/${seriesId}/finish`);
};

// FINISHED SERIES: bring a finished series back to the dropdown
export const reopenSeries = async (seriesId: number): Promise<void> => {
    await api.post(`/api/scripts/series/${seriesId}/reopen`);
};

// ---------- TIMED VOICEOVER ----------

// Saves each shot's clip length, for example { 1: 10, 2: 8 }
export const saveShotLengths = async (scriptId: number, lengths: Record<number, number>) => {
    const response = await api.put(`/api/scripts/${scriptId}/shot-lengths`, { lengths });
    return response.data;
};

// Makes (or remakes) the timed voiceover; uses the same options as the normal voiceover
export const generateTimedVoiceover = async (
    scriptId: number,
    voiceName: string | null,
    voiceDescription: string | null,
    includeDialogue: boolean,
) => {
    const response = await api.post(
        `/api/scripts/${scriptId}/timed-voiceover`,
        { voiceName, voiceDescription, includeDialogue },
        { timeout: 300000 }, // up to 5 minutes, since every shot is voiced separately
    );
    return response.data;
};

// Details of the saved timed voiceover, or null if there is none yet
export const getTimedVoiceover = async (scriptId: number) => {
    const response = await api.get(`/api/scripts/${scriptId}/timed-voiceover`);
    return response.status === 204 ? null : response.data;
};

// Loads the timed MP3 and returns a link the audio player can use
export const getTimedVoiceoverAudioUrl = async (scriptId: number): Promise<string> => {
    const response = await api.get(`/api/scripts/${scriptId}/timed-voiceover/audio`, {
        responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
};